import { ConfirmDialog } from "@/components/confirm-dialog";
import { DataTable } from "@/components/data-table";
import { EmptyState } from "@/components/empty-state";
import { PageHeader } from "@/components/page-header";
import { TableSkeleton } from "@/components/skeleton";
import { StatusDot } from "@/components/status-dot";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ErrorState } from "@/components/ui/error-state";
import { useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { FolderKanban, MoreHorizontal } from "lucide-react";
import { useState } from "react";
import { CreateProjectDialog } from "./create-dialog";
import { useCurrentProject } from "./current-project";
import { useDeleteProject, useRenameProject } from "./mutations";
import { type Project, useProjects } from "./queries";

function formatRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.round(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

export function ProjectsListPage() {
  const { data, isLoading, isError, refetch } = useProjects();
  const { setCurrent } = useCurrentProject();
  const navigate = useNavigate();

  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Project | null>(null);
  const rename = useRenameProject();
  const del = useDeleteProject();

  const handleRename = (p: Project) => {
    // Quick-and-dirty rename via browser prompt
    const next = window.prompt("New project name:", p.name);
    if (next?.trim() && next.trim() !== p.name) {
      rename.mutate({ id: p.id, name: next.trim() });
    }
  };

  const columns: ColumnDef<Project>[] = [
    {
      header: "Name",
      accessorKey: "name",
      cell: ({ row }) => (
        <span className="inline-flex items-center gap-2 font-medium">
          <StatusDot status="active" label="" />
          {row.original.name}
        </span>
      ),
    },
    {
      header: "Updated",
      cell: ({ row }) => (
        <span className="mono text-fg-muted">{formatRelative(row.original.updated_at)}</span>
      ),
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => {
        const p = row.original;
        return (
          <div className="flex justify-end">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={(e) => e.stopPropagation()}
                  aria-label="Project actions"
                >
                  <MoreHorizontal size={14} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => handleRename(p)}>Rename</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem variant="danger" onSelect={() => setDeleteTarget(p)}>
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    },
  ];

  if (isLoading)
    return (
      <>
        <PageHeader
          title="Projects"
          subtitle="Manage isolated workspaces — datasets, models, keys"
        />
        <TableSkeleton />
      </>
    );

  if (isError)
    return (
      <>
        <PageHeader title="Projects" />
        <ErrorState message="Failed to load projects" onRetry={() => refetch()} />
      </>
    );

  const items = data?.items ?? [];
  return (
    <>
      <PageHeader
        title="Projects"
        subtitle="Manage isolated workspaces — datasets, models, keys"
        actions={
          <Button variant="primary" onClick={() => setCreateOpen(true)}>
            + New project
          </Button>
        }
      />
      <DataTable
        data={items}
        columns={columns}
        onRowClick={(p) => {
          setCurrent(p.id, p.name);
          navigate({ to: "/overview" });
        }}
        emptyState={
          <EmptyState
            icon={<FolderKanban size={24} />}
            title="No projects yet"
            description="Create your first project to start uploading datasets and training models."
          />
        }
      />
      <CreateProjectDialog open={createOpen} onOpenChange={setCreateOpen} />
      {deleteTarget && (
        <ConfirmDialog
          open={!!deleteTarget}
          onOpenChange={(o) => {
            if (!o) setDeleteTarget(null);
          }}
          title={`Delete "${deleteTarget.name}"?`}
          description="This will permanently delete the project and all associated resources. This action cannot be undone."
          confirmLabel="Delete project"
          tone="danger"
          requireText={deleteTarget.name}
          busy={del.isPending}
          onConfirm={async () => {
            await del.mutateAsync(deleteTarget.id);
            setDeleteTarget(null);
          }}
        />
      )}
    </>
  );
}
